/** @module csi/widget/DateRange */
define([
	"dojo/_base/declare",
	"dojo/keys",
	"dojo/on",
	"dijit/_HasDropDown",
	"dijit/form/MappedTextBox",
	"dijit/popup",
	"csi/_base/underscore",
	"csi/locale",
	"csi/types",
	"./DateRangePopup",
	"dojo/text!dijit/form/templates/DropDownBox.html"
], function(declare,keys,on,_HasDropDown,MappedTextBox,popup,_,locale,types,DateRangePopup,template) {

	return declare([MappedTextBox,_HasDropDown], {
		baseClass: "dijitTextBox dijitComboBox csi-date-range",
		popupClass: DateRangePopup,
		templateString: template,

		autoWidth: false,
		hasDownArrow: true,
		maxHeight: Infinity,

		cssStateNodes: {
			"_buttonNode": "dijitDownArrowButton"
		},

		/** @property {boolean} bOpenOnFocus - if true then open drop-down on focus */
		bOpenOnFocus: false,

		/** @property {Object} popupParams - parameters to set on popup class when created */
		popupParams: null,

		/** @property {Object} query - query object to filter data in store */
		query: null,
		
		/** @property {Store} store - values store of existing date values */
		store: null,

		/** @property {Object} _formatMap - map of formatted values to internal values; necessary due to complexity of _TextBoxMixin::_setValueAttr  */
		_formatMap: null,

		/**
		 * Set default value for placeholder and create drop-down
		 */
		postMixInProperties: function() {
			this._formatMap = {};
			this.inherited(arguments);
			this.placeHolder = this.placeHolder || locale.resource("Select");
			this.dropDown = new this.popupClass(_.extend({
				query: this.query,
				store: this.store,
				value: this.value
			}, this.popupParams));
		},

		/**
		 * Watch for changes to popupParams and value, and forward them to DateRangePopup
		 */
		postCreate: function() {
			var dropDown = this.dropDown;

			this.inherited(arguments);

			this.watch("popupParams", function(attr, oldValue, newValue) {
				dropDown.set(newValue);
			});

			this.watch("value", function(attr, oldValue, newValue) {
				dropDown.set("value", newValue);
			});
		},

		/**
		 * Close popup and update values appropriately
		 * @param {boolean} focus
		 */
		closeDropDown: function(focus) {
			// handle ESCAPE from text box and restore existing value on drop down
			if (focus === undefined) {
				this.dropDown.set("value", this.value);
			}

			if (this._keyHandler) {
				this._keyHandler.remove();
				this._keyHandler = null;
			}

			this.inherited(arguments);
		},

		/**
		 * Convert widget "value", which is an array, into "displayedValue" which is a string, and track in map so parse can do the opposite
		 * @param {string} value
		 * @param {Object} [constraints]
		 * @return {string}
		 */
		format: function(value, constraints) {
			var displayedValue = types.formatDateRange(value);
			this._formatMap[displayedValue] = _.clone(value);
			return displayedValue;
		},

		/**
		 * Extension point indicating ESCAPE key used
		 */
		onEscape: function() {},

		/**
		 * Prevent any text input to widget except TAB (down arrow and escape to open and close popup still seem to work)
		 * @param {Event} evt
		 * @return {boolean} return false to cancel event
		 */
		onInput: function(evt) {
			// if dropDown not open, then trap escape key as well
			if ((evt.keyCode == keys.ESCAPE) && !this._keyHandler) {
				this.onEscape();
				return false;
			}

			return evt.keyCode == keys.TAB;
		},

		/**
		 * Override to separate ESCAPE from TAB handling
		 */
		openDropDown: function() {
			var ret = this.inherited(arguments);
			var self = this;

			// override key handling from popup.open; TAB key should not cancel
			this._keyHandler = on(this.dropDown.domNode, "keydown", function(evt) {
				var bEscape = (evt.keyCode == keys.ESCAPE);
				var bTab = (evt.keyCode == keys.TAB);

				if (bTab || bEscape) {
					evt.stopPropagation();
					evt.preventDefault();
				}

				if (bTab) {
					// handle tabbing between popup widgets
					self.dropDown.onTab(evt.shiftKey);
				} else if (bEscape) {
					// close and reset value on drop down
					self.closeDropDown(true);
					self.dropDown.set("value", self.value);
					self.onEscape();
				}
			});

			return ret;
		},

		/**
		 * Override _TextBoxMixin to just return current value rather than parsing displayed value. Since this
		 * widget is read-only (cannot enter text, only populate using popup), this shouldn't be a problem.
		 * @param {string} displayedValue
		 * @param {Object} [constraints]
		 * @return {string}
		 */
		parse: function(displayedValue, constaints) {
			return this._formatMap[displayedValue] || this.value;
		},

		/**
		 * Open drop down on focus if so configured
		 */
		_onFocus: function() {
			if (!this._opened && this.bOpenOnFocus) {
				this.openDropDown();
			}

			this.inherited(arguments);
		},

		/**
		 * Set value of widget to that of drop-down; defer to wait for final onChange events to catch up
		 */
		_setBlurValue: function() {
			_.defer(function(self) {
				self.set("value", self.dropDown.get("value"));
			}, this);
		},
		
		/**
		 * Pass new query value to drop down
		 */
		_setQueryAttr: function(value) {
			this._set("query", value);
			
			if (this.dropDown) {
				this.dropDown.set("query", value);
			}
		},

		/**
		 * Pass new store value to drop down
		 */
		_setStoreAttr: function(value) {
			this._set("store", value);

			if (this.dropDown) {
				this.dropDown.set("store", value);
			}
		}
	});
});
